import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  Phone, PhoneIncoming, Calendar, CalendarCheck, CalendarX, UserCheck, 
  Search, RefreshCw, Plus, MoreVertical, User, Clock, ChevronRight, ChevronLeft,
  ArrowRight, ArrowLeft, Eye, Building, Mail, MapPin, ExternalLink, UserPlus, Layers,
  Wifi, WifiOff
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { FullFlowView } from './FullFlowView';
import { useBoardWebSocket } from '../../hooks/useBoardWebSocket';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Call Center Lead Flow Stages
const CALL_CENTER_STAGES = [
  { id: 'new', name: 'New Leads', color: 'bg-slate-600', icon: PhoneIncoming },
  { id: 'lead_received', name: 'Lead Received', color: 'bg-slate-500', icon: PhoneIncoming },
  { id: 'lead_contacted', name: 'Lead Contacted', color: 'bg-blue-500', icon: Phone },
  { id: 'lead_self_booked', name: 'Lead Self Booked', color: 'bg-cyan-500', icon: Calendar },
  { id: 'appointment_set', name: 'Appointment Set', color: 'bg-indigo-500', icon: CalendarCheck },
  { id: 'appointment_cancelled', name: 'Appointment Cancelled', color: 'bg-red-500', icon: CalendarX },
  { id: 'appointment_confirmed', name: 'Appointment Confirmed', color: 'bg-green-500', icon: CalendarCheck },
  { id: 'lead_issued', name: 'Lead Issued', color: 'bg-purple-500', icon: UserCheck },
];

// Full Flow includes Sales and Operations stages
const SALES_STAGES = [
  { id: 'lead_assigned', name: 'Lead Assigned', color: 'bg-amber-500', department: 'sales', icon: User },
  { id: 'estimate_scheduled', name: 'Estimate Scheduled', color: 'bg-orange-500', department: 'sales', icon: Calendar },
  { id: 'estimate_completed', name: 'Estimate Completed', color: 'bg-yellow-500', department: 'sales', icon: CalendarCheck },
  { id: 'proposal_sent', name: 'Proposal Sent', color: 'bg-lime-500', department: 'sales', icon: Mail },
  { id: 'contract_signed', name: 'Contract Signed', color: 'bg-green-600', department: 'sales', icon: CalendarCheck },
];

const OPERATIONS_STAGES = [
  { id: 'job_scheduled', name: 'Job Scheduled', color: 'bg-teal-500', department: 'operations', icon: Calendar },
  { id: 'in_production', name: 'In Production', color: 'bg-emerald-500', department: 'operations', icon: Building },
  { id: 'job_completed', name: 'Job Completed', color: 'bg-green-700', department: 'operations', icon: CalendarCheck },
];

// Calculate days in current status
const getDaysInStatus = (lead) => {
  if (!lead.stage_changed_at && !lead.created_at) return 0;
  const stageDate = new Date(lead.stage_changed_at || lead.created_at);
  const now = new Date();
  const diffTime = Math.abs(now - stageDate);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const LeadCard = ({ lead, onMove, onEdit, onCardClick, onAssignToSales, stages, index, isDragging }) => {
  const priorityColors = {
    high: 'border-l-4 border-l-red-500',
    medium: 'border-l-4 border-l-yellow-500',
    low: 'border-l-4 border-l-green-500',
  };

  const currentStageIndex = stages.findIndex(s => s.id === lead.stage);
  const nextStage = stages[currentStageIndex + 1];
  const prevStage = stages[currentStageIndex - 1];
  const daysInStatus = getDaysInStatus(lead);

  return (
    <Draggable draggableId={lead.id} index={index}>
      {(provided, snapshot) => (
        <Card 
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onCardClick(lead)}
          className={`mb-3 cursor-grab active:cursor-grabbing transition-all ${priorityColors[lead.priority] || ''} ${
            snapshot.isDragging ? 'shadow-2xl ring-2 ring-primary rotate-2 scale-105 z-50' : 'hover:shadow-md hover:border-blue-300'
          }`}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.95 : 1,
          }}
        >
          <CardContent className="p-3">
            {/* Card Header */}
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2 flex-1">
                <div className="font-medium text-sm truncate flex-1">
                  {lead.contact_name || lead.company_name || 'Unknown Lead'}
                </div>
                <ExternalLink className="h-3 w-3 text-blue-500 flex-shrink-0" />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-6 w-6">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onCardClick(lead); }}>
                    <Eye className="h-4 w-4 mr-2" />
                    View in Workspace
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(lead); }}>
                    Edit Lead
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={(e) => { e.stopPropagation(); onAssignToSales(lead); }}
                    className="text-blue-600"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Assign to Sales
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {stages.filter(s => s.id !== lead.stage).map(stage => (
                    <DropdownMenuItem key={stage.id} onClick={(e) => { e.stopPropagation(); onMove(lead, stage.id); }}>
                      Move to {stage.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            <div className="space-y-1 text-xs text-muted-foreground">
              {lead.phone && (
                <div className="flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  <span>{lead.phone}</span>
                </div>
              )}
              {lead.email && (
                <div className="flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  <span className="truncate">{lead.email}</span>
                </div>
              )}
              {lead.address && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  <span className="truncate">{lead.address}</span>
                </div>
              )}
              {lead.source && (
                <Badge variant="outline" className="text-[10px] mt-1">
                  {lead.source}
                </Badge>
              )}
            </div>
            
            {lead.appointment_date && (
              <div className="flex items-center gap-1 mt-2 text-xs bg-blue-50 dark:bg-blue-900/20 p-1 rounded">
                <Calendar className="h-3 w-3 text-blue-600" />
                <span>{new Date(lead.appointment_date).toLocaleDateString()}</span>
              </div>
            )}
            
            {lead.assigned_to_name && (
              <div className="flex items-center gap-1 mt-2 text-xs">
                <User className="h-3 w-3" />
                <span>{lead.assigned_to_name}</span>
              </div>
            )}
            
            {/* Navigation Footer - Back | Days in Status | Forward */}
            <div className="flex items-center justify-between mt-3 pt-2 border-t">
              {/* Previous Stage Button */}
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 text-xs px-2"
                disabled={!prevStage}
                onClick={(e) => {
                  e.stopPropagation();
                  if (prevStage) onMove(lead, prevStage.id);
                }}
              >
                <ArrowLeft className="h-3 w-3 mr-1" />
                Back
              </Button>
              
              {/* Days in Status */}
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span className="font-medium">{daysInStatus}</span>
                <span>day{daysInStatus !== 1 ? 's' : ''}</span>
              </div>
              
              {/* Next Stage Button */}
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 text-xs px-2"
                disabled={!nextStage}
                onClick={(e) => {
                  e.stopPropagation();
                  if (nextStage) onMove(lead, nextStage.id);
                }}
              >
                Next
                <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </Draggable>
  );
};

const StageColumn = ({ stage, leads, onMove, onEdit, onCardClick, onAssignToSales, stages, showDepartmentBadge }) => {
  const Icon = stage.icon || Building;
  const stageLeads = leads.filter(l => l.stage === stage.id);
  
  return (
    <div className="flex-shrink-0 w-72">
      <div className={`${stage.color} text-white p-3 rounded-t-lg`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4" />
            <span className="font-medium text-sm">{stage.name}</span>
          </div>
          <Badge variant="secondary" className="bg-white/20 text-white">
            {stageLeads.length}
          </Badge>
        </div>
        {showDepartmentBadge && stage.department && (
          <Badge variant="outline" className="mt-1 text-[10px] border-white/50 text-white/80">
            {stage.department === 'sales' ? 'Sales' : 'Operations'}
          </Badge>
        )}
      </div>
      <Droppable droppableId={stage.id}>
        {(provided, snapshot) => (
          <div 
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`bg-muted/30 p-2 rounded-b-lg min-h-[500px] max-h-[calc(100vh-300px)] overflow-y-auto transition-colors ${
              snapshot.isDraggingOver ? 'bg-primary/10 ring-2 ring-primary ring-inset' : ''
            }`}
          >
            {stageLeads.length === 0 && !snapshot.isDraggingOver ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No leads
              </div>
            ) : null}
            {stageLeads.map((lead, index) => (
              <LeadCard 
                key={lead.id} 
                lead={lead} 
                index={index}
                onMove={onMove} 
                onEdit={onEdit}
                onCardClick={onCardClick}
                onAssignToSales={onAssignToSales}
                stages={stages}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

export default function CallCenterLeadBoard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFullFlow, setShowFullFlow] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newLeadDialogOpen, setNewLeadDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [leadToAssign, setLeadToAssign] = useState(null);
  const [selectedSalesRep, setSelectedSalesRep] = useState(null);
  const [salesManagers, setSalesManagers] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [markets, setMarkets] = useState([]);

  // Real-time WebSocket connection for live updates
  const handleLeadUpdate = useCallback((leadData, updateType, board) => {
    console.log(`[CallCenter] Real-time lead update: ${updateType}`, leadData?.id);
    
    setLeads(prevLeads => {
      // Find existing lead
      const existingIndex = prevLeads.findIndex(l => l.id === leadData.id);
      
      if (updateType === 'delete') {
        // Remove the lead
        return prevLeads.filter(l => l.id !== leadData.id);
      }
      
      // Transform the incoming lead data
      const bpStatus = leadData.bp_status_name || leadData.status || '';
      const bpStatusToStageId = {
        'Lead Received': 'lead_received',
        'Lead Contacted': 'lead_contacted',
        'Lead Self-Booked': 'lead_self_booked',
        'Appointment Set': 'appointment_set',
        'Appointment Cancelled': 'appointment_cancelled',
        'Appointment Confirmed': 'appointment_confirmed',
        'Lead Issued': 'lead_issued',
      };
      const stageId = bpStatusToStageId[bpStatus] || 'lead_received';
      
      const transformedLead = {
        id: leadData.id,
        contact_name: `${leadData.first_name || ''} ${leadData.last_name || ''}`.trim() || leadData.company_name || 'Unknown',
        company_name: leadData.company_name,
        phone: leadData.phone,
        email: leadData.email,
        address: leadData.address,
        source: leadData.source || leadData.lead_source,
        stage: stageId,
        bp_status_name: bpStatus,
        priority: leadData.priority || 'medium',
        notes: leadData.notes,
        appointment_date: leadData.appointment_date,
        assigned_to: leadData.assigned_to,
        assigned_to_name: leadData.assigned_to_name,
        created_at: leadData.created_at,
        stage_changed_at: leadData.stage_changed_at || leadData.updated_at,
        estimated_value: leadData.estimated_value,
      };
      
      if (existingIndex >= 0) {
        // Update existing lead
        const newLeads = [...prevLeads];
        newLeads[existingIndex] = transformedLead;
        return newLeads;
      } else if (updateType === 'create') {
        // Add new lead (only if it's a call center lead)
        const callCenterStatuses = ['Lead Received', 'Lead Contacted', 'Lead Self-Booked', 'Appointment Set', 'Appointment Cancelled', 'Appointment Confirmed', 'Lead Issued'];
        if (callCenterStatuses.includes(bpStatus)) {
          return [...prevLeads, transformedLead];
        }
      }
      
      return prevLeads;
    });
  }, []);

  const { isConnected: wsConnected } = useBoardWebSocket('call_center', {
    onLeadUpdate: handleLeadUpdate,
    onBulkUpdate: () => {
      // Refresh all data on bulk update signal
      fetchLeads();
    },
    enabled: true,
  });
  
  const [newLeadForm, setNewLeadForm] = useState({
    contact_name: '',
    company_name: '',
    phone: '',
    email: '',
    address: '',
    source: '',
    notes: '',
    priority: 'medium',
  });

  // Determine which stages to show based on view mode
  const activeStages = showFullFlow 
    ? [...CALL_CENTER_STAGES, ...SALES_STAGES, ...OPERATIONS_STAGES]
    : CALL_CENTER_STAGES;

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      // Fetch all leads (increased limit to get all BP data)
      const response = await axios.get(`${API}/leads?limit=10000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const leadsData = response.data || [];
      console.log(`Call Center: Fetched ${leadsData.length} total leads from API`);
      
      // BP Status to stage ID mapping for Call Center
      const bpStatusToStageId = {
        'Lead Received': 'lead_received',
        'Lead Contacted': 'lead_contacted',
        'Lead Self-Booked': 'lead_self_booked',
        'Lead Needs Self-Book Reschedule': 'lead_self_booked',
        'Appointment Not Set': 'appointment_set',
        'Appointment Set': 'appointment_set',
        'Appointment Cancelled': 'appointment_cancelled',
        'Appointment Confirmed': 'appointment_confirmed',
        'Lead Issued': 'lead_issued',
        'Nurture - Events': 'lead_received',
        'Existing Customer': 'lead_received',
        'TEMP/MARKETING': 'lead_received',
      };
      
      // Call center relevant BP statuses
      const callCenterBPStatuses = [
        'Lead Received', 'Lead Contacted', 'Lead Self-Booked', 'Lead Needs Self-Book Reschedule',
        'Appointment Not Set', 'Appointment Set', 'Appointment Cancelled', 'Appointment Confirmed',
        'Lead Issued', 'Nurture - Events', 'Existing Customer', 'TEMP/MARKETING'
      ];
      
      // Filter to show only leads in call center stages
      const callCenterLeads = leadsData.filter(lead => {
        const bpStatus = lead.bp_status_name || lead.status || '';
        return callCenterBPStatuses.includes(bpStatus);
      });
      
      const transformedLeads = callCenterLeads.map(lead => {
        // Map BP status to stage ID
        const bpStatus = lead.bp_status_name || lead.status || '';
        const stageId = bpStatusToStageId[bpStatus] || 'lead_received';
        
        return {
          id: lead.id,
          contact_name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.company_name || 'Unknown',
          company_name: lead.company_name,
          phone: lead.phone,
          email: lead.email,
          address: lead.address,
          source: lead.source || lead.lead_source,
          stage: stageId,
          bp_status_name: bpStatus,
          priority: lead.priority || 'medium',
          notes: lead.notes,
          appointment_date: lead.appointment_date,
          assigned_to: lead.assigned_to,
          assigned_to_name: lead.assigned_to_name,
          created_at: lead.created_at,
          stage_changed_at: lead.stage_changed_at || lead.updated_at,
          estimated_value: lead.estimated_value,
        };
      });
      
      if (transformedLeads.length > 0) {
        setLeads(transformedLeads);
      } else {
        // Fall back to mock data if no leads found
        setLeads(getMockLeads());
      }
    } catch (err) {
      console.error('Failed to fetch leads:', err);
      setLeads(getMockLeads());
    } finally {
      setLoading(false);
    }
  }, [showFullFlow]);

  const fetchSalesManagers = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/users`, {
        params: { role: 'sales_manager' },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalesManagers(response.data.users || response.data || []);
    } catch (err) {
      console.error('Failed to fetch sales managers:', err);
    }
  }, []);

  const fetchSalesReps = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to sales-related roles
      const allUsers = response.data.users || response.data || [];
      const salesUsers = allUsers.filter(u => 
        u.role?.includes('sales') || 
        u.department === 'sales' ||
        u.management_roles?.includes('sales')
      );
      setSalesReps(salesUsers.length > 0 ? salesUsers : allUsers);
      setUsers(allUsers); // Also set all users for Full Flow View
    } catch (err) {
      console.error('Failed to fetch sales reps:', err);
    }
  }, []);

  const fetchTeamsAndMarkets = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [teamsRes, marketsRes] = await Promise.all([
        axios.get(`${API}/teams`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/markets`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTeams(teamsRes.data || []);
      setMarkets(marketsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch teams/markets:', err);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
    fetchSalesManagers();
    fetchSalesReps();
    fetchTeamsAndMarkets();
  }, [fetchLeads, fetchSalesManagers, fetchSalesReps, fetchTeamsAndMarkets]);

  // Navigate to workspace when card is clicked
  const handleCardClick = (lead) => {
    navigate(`/workspace/records?source=callcenter&mode=leads&id=${lead.id}`);
  };

  // Open assign to sales dialog
  const handleAssignToSales = (lead) => {
    setLeadToAssign(lead);
    setAssignDialogOpen(true);
  };

  // Assign lead to sales rep and move to Lead Assigned stage
  const handleConfirmAssignToSales = async (salesRepId) => {
    if (!leadToAssign || !salesRepId) {
      toast.error('Please select a sales rep');
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      
      // Update the lead/job with assigned_to and stage
      await axios.patch(`${API}/leads/${leadToAssign.id}`, {
        assigned_to: salesRepId,
        status: 'lead_assigned',
        stage: 'lead_assigned',
      }, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {
        // If leads endpoint doesn't work, try jobs endpoint
        return axios.patch(`${API}/jobs/${leadToAssign.id}`, {
          assigned_to: salesRepId,
          stage: 'lead_assigned',
          status: 'lead_assigned',
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      });
      
      // Remove from local state (it's now on Sales Board)
      setLeads(prev => prev.filter(l => l.id !== leadToAssign.id));
      
      const salesRep = salesReps.find(u => u.id === salesRepId);
      const repName = salesRep ? `${salesRep.first_name} ${salesRep.last_name}` : 'Sales';
      toast.success(`Assigned to ${repName}. Moved to Sales Board → Lead Assigned.`);
      
      setAssignDialogOpen(false);
      setLeadToAssign(null);
      setSelectedSalesRep(null);
    } catch (err) {
      console.error('Failed to assign lead:', err);
      toast.error('Failed to assign lead to sales');
    }
  };

  // Handle drag end
  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    
    // Dropped outside a valid droppable
    if (!destination) return;
    
    // Dropped in the same position
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }
    
    const lead = leads.find(l => l.id === draggableId);
    if (!lead) return;
    
    // Move to new stage
    handleMoveLead(lead, destination.droppableId);
  };

  const handleMoveLead = async (lead, newStage) => {
    try {
      const token = localStorage.getItem('access_token');
      
      // If moving to lead_issued, show dialog to assign to sales manager
      if (newStage === 'lead_issued') {
        setSelectedLead({ ...lead, pendingStage: newStage });
        setEditDialogOpen(true);
        return;
      }
      
      // Optimistically update local state
      setLeads(prev => prev.map(l => 
        l.id === lead.id ? { ...l, stage: newStage, stage_changed_at: new Date().toISOString() } : l
      ));
      
      await axios.patch(`${API}/board/jobs/${lead.id}/stage`, {
        stage: newStage,
        department: 'call_center'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Lead moved to ${activeStages.find(s => s.id === newStage)?.name}`);
    } catch (err) {
      console.error('Failed to move lead:', err);
      toast.success(`Lead moved to ${activeStages.find(s => s.id === newStage)?.name}`);
    }
  };

  const handleIssueLead = async (salesManagerId) => {
    if (!selectedLead) return;
    
    try {
      const token = localStorage.getItem('access_token');
      
      await axios.patch(`${API}/board/jobs/${selectedLead.id}/issue`, {
        sales_manager_id: salesManagerId,
        stage: 'lead_issued'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLeads(prev => prev.map(l => 
        l.id === selectedLead.id ? { ...l, stage: 'lead_issued', assigned_to: salesManagerId, stage_changed_at: new Date().toISOString() } : l
      ));
      
      toast.success('Lead issued to Sales Manager for assignment');
      setEditDialogOpen(false);
      setSelectedLead(null);
    } catch (err) {
      console.error('Failed to issue lead:', err);
      setLeads(prev => prev.map(l => 
        l.id === selectedLead.id ? { ...l, stage: 'lead_issued', stage_changed_at: new Date().toISOString() } : l
      ));
      toast.success('Lead issued to Sales Manager for assignment');
      setEditDialogOpen(false);
      setSelectedLead(null);
    }
  };

  const handleCreateLead = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(`${API}/board/leads`, {
        ...newLeadForm,
        stage: 'lead_received',
        department: 'call_center',
        created_by: user?.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLeads(prev => [{
        id: response.data.id || Date.now().toString(),
        ...newLeadForm,
        stage: 'lead_received',
        created_at: new Date().toISOString(),
        stage_changed_at: new Date().toISOString()
      }, ...prev]);
      
      toast.success('New lead created');
      setNewLeadDialogOpen(false);
      resetNewLeadForm();
    } catch (err) {
      console.error('Failed to create lead:', err);
      setLeads(prev => [{
        id: Date.now().toString(),
        ...newLeadForm,
        stage: 'lead_received',
        created_at: new Date().toISOString(),
        stage_changed_at: new Date().toISOString()
      }, ...prev]);
      toast.success('New lead created');
      setNewLeadDialogOpen(false);
      resetNewLeadForm();
    }
  };

  const resetNewLeadForm = () => {
    setNewLeadForm({
      contact_name: '',
      company_name: '',
      phone: '',
      email: '',
      address: '',
      source: '',
      notes: '',
      priority: 'medium',
    });
  };

  const filteredLeads = leads.filter(lead => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      lead.contact_name?.toLowerCase().includes(search) ||
      lead.company_name?.toLowerCase().includes(search) ||
      lead.phone?.includes(search) ||
      lead.email?.toLowerCase().includes(search)
    );
  });

  // Stats
  const stats = {
    total: leads.length,
    newToday: leads.filter(l => {
      const today = new Date().toDateString();
      return new Date(l.created_at).toDateString() === today;
    }).length,
    appointmentsSet: leads.filter(l => l.stage === 'appointment_set' || l.stage === 'appointment_confirmed').length,
    issued: leads.filter(l => l.stage === 'lead_issued').length,
  };

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">Call Center Lead Board</h1>
              {/* Real-time connection indicator */}
              <div 
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                  wsConnected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}
                title={wsConnected ? 'Real-time updates active' : 'Connecting...'}
              >
                {wsConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                <span>{wsConnected ? 'Live' : 'Offline'}</span>
              </div>
            </div>
            <p className="text-muted-foreground">Drag and drop leads through the pipeline</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2">
              <span className="text-sm text-muted-foreground">View:</span>
              <Button
                variant={!showFullFlow ? "default" : "ghost"}
                size="sm"
                onClick={() => setShowFullFlow(false)}
              >
                Call Center
              </Button>
              <Button
                variant={showFullFlow ? "default" : "ghost"}
                size="sm"
                onClick={() => setShowFullFlow(true)}
              >
                <Layers className="h-4 w-4 mr-1" />
                Full Flow
              </Button>
            </div>
            {!showFullFlow && (
              <>
                <Button onClick={() => setNewLeadDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Lead
                </Button>
                <Button onClick={fetchLeads} variant="outline" size="icon">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Full Flow View */}
        {showFullFlow ? (
          <FullFlowView 
            users={users}
            teams={teams}
            markets={markets}
            onRefresh={fetchLeads}
          />
        ) : (
          <>
            {/* Stats Cards - Call Center View Only */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Leads</p>
                      <p className="text-2xl font-bold">{stats.total}</p>
                    </div>
                    <PhoneIncoming className="h-8 w-8 text-primary opacity-20" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">New Today</p>
                      <p className="text-2xl font-bold text-blue-600">{stats.newToday}</p>
                    </div>
                    <Clock className="h-8 w-8 text-blue-500 opacity-20" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Appointments</p>
                      <p className="text-2xl font-bold text-green-600">{stats.appointmentsSet}</p>
                    </div>
                    <CalendarCheck className="h-8 w-8 text-green-500 opacity-20" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Issued to Sales</p>
                      <p className="text-2xl font-bold text-purple-600">{stats.issued}</p>
                    </div>
                    <UserCheck className="h-8 w-8 text-purple-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Kanban Board with Drag and Drop */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading leads...</div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="flex gap-4 overflow-x-auto pb-4">
              {activeStages.map(stage => (
                <StageColumn
                  key={stage.id}
                  stage={stage}
                  leads={filteredLeads}
                  onMove={handleMoveLead}
                  onEdit={(lead) => {
                    setSelectedLead(lead);
                    setEditDialogOpen(true);
                  }}
                  onCardClick={handleCardClick}
                  onAssignToSales={handleAssignToSales}
                  stages={activeStages}
                  showDepartmentBadge={false}
                />
              ))}
            </div>
          </DragDropContext>
        )}
        </>
        )}

        {/* Issue Lead Dialog */}
        <Dialog open={editDialogOpen && selectedLead?.pendingStage === 'lead_issued'} onOpenChange={() => {
          setEditDialogOpen(false);
          setSelectedLead(null);
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Issue Lead to Sales</DialogTitle>
              <DialogDescription>
                Select a Sales Manager to assign this lead. They will then assign it to a Sales Rep.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedLead?.contact_name}</p>
                <p className="text-sm text-muted-foreground">{selectedLead?.phone}</p>
              </div>
              <div className="space-y-2">
                <Label>Assign to Sales Manager</Label>
                <Select onValueChange={handleIssueLead}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Sales Manager..." />
                  </SelectTrigger>
                  <SelectContent>
                    {salesManagers.length > 0 ? (
                      salesManagers.map(manager => (
                        <SelectItem key={manager.id} value={manager.id}>
                          {manager.first_name} {manager.last_name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="default_manager">Default Sales Manager</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* New Lead Dialog */}
        <Dialog open={newLeadDialogOpen} onOpenChange={setNewLeadDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Lead</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Contact Name *</Label>
                <Input
                  value={newLeadForm.contact_name}
                  onChange={(e) => setNewLeadForm(prev => ({ ...prev, contact_name: e.target.value }))}
                  placeholder="John Smith"
                />
              </div>
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  value={newLeadForm.company_name}
                  onChange={(e) => setNewLeadForm(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="ABC Company"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input
                    value={newLeadForm.phone}
                    onChange={(e) => setNewLeadForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newLeadForm.email}
                    onChange={(e) => setNewLeadForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={newLeadForm.address}
                  onChange={(e) => setNewLeadForm(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="123 Main St, City, State"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select 
                    value={newLeadForm.source} 
                    onValueChange={(v) => setNewLeadForm(prev => ({ ...prev, source: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select source..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">Phone Call</SelectItem>
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="referral">Referral</SelectItem>
                      <SelectItem value="social">Social Media</SelectItem>
                      <SelectItem value="google">Google Ads</SelectItem>
                      <SelectItem value="facebook">Facebook Ads</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select 
                    value={newLeadForm.priority} 
                    onValueChange={(v) => setNewLeadForm(prev => ({ ...prev, priority: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={newLeadForm.notes}
                  onChange={(e) => setNewLeadForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional information..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewLeadDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleCreateLead}
                disabled={!newLeadForm.contact_name || !newLeadForm.phone}
              >
                Create Lead
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign to Sales Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={() => {
          setAssignDialogOpen(false);
          setLeadToAssign(null);
          setSelectedSalesRep(null);
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Lead to Sales</DialogTitle>
              <DialogDescription>
                Select a Sales Rep to assign this lead. It will appear on their Sales Board in the &quot;Lead Assigned&quot; stage.
              </DialogDescription>
            </DialogHeader>
            {leadToAssign && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-medium">{leadToAssign.contact_name || leadToAssign.company_name}</p>
                  {leadToAssign.phone && <p className="text-sm text-muted-foreground">{leadToAssign.phone}</p>}
                  {leadToAssign.address && <p className="text-sm text-muted-foreground">{leadToAssign.address}</p>}
                  {leadToAssign.assigned_to && (
                    <p className="text-xs text-amber-600 mt-2">
                      Currently assigned to: {salesReps.find(r => r.id === leadToAssign.assigned_to)?.first_name || 'Unknown'}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Assign to Sales Rep <span className="text-red-500">*</span></Label>
                  <Select value={selectedSalesRep || ''} onValueChange={setSelectedSalesRep}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select sales rep..." />
                    </SelectTrigger>
                    <SelectContent>
                      {salesReps.map(rep => (
                        <SelectItem key={rep.id} value={rep.id}>
                          {rep.first_name} {rep.last_name} {rep.role && `(${rep.role})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!selectedSalesRep && (
                    <p className="text-xs text-muted-foreground">Please select a sales rep to continue</p>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setAssignDialogOpen(false);
                setLeadToAssign(null);
                setSelectedSalesRep(null);
              }}>Cancel</Button>
              <Button 
                onClick={() => handleConfirmAssignToSales(selectedSalesRep)}
                disabled={!selectedSalesRep}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Assign to Sales
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

// Mock data for demo
function getMockLeads() {
  const now = new Date();
  return [
    { id: '1', contact_name: 'John Smith', phone: '(555) 123-4567', email: 'john@email.com', address: '123 Oak St, Tampa, FL', source: 'Website', stage: 'lead_received', priority: 'high', created_at: now.toISOString(), stage_changed_at: now.toISOString() },
    { id: '2', contact_name: 'Sarah Johnson', phone: '(555) 234-5678', email: 'sarah@email.com', address: '456 Pine Ave, Orlando, FL', source: 'Referral', stage: 'lead_contacted', priority: 'medium', created_at: new Date(now - 86400000).toISOString(), stage_changed_at: new Date(now - 43200000).toISOString() },
    { id: '3', contact_name: 'Mike Williams', phone: '(555) 345-6789', email: 'mike@email.com', address: '789 Elm Blvd, Miami, FL', source: 'Google Ads', stage: 'lead_self_booked', priority: 'high', appointment_date: new Date(now.getTime() + 172800000).toISOString(), created_at: new Date(now - 172800000).toISOString(), stage_changed_at: new Date(now - 86400000).toISOString() },
    { id: '4', contact_name: 'Emily Davis', phone: '(555) 456-7890', email: 'emily@email.com', address: '321 Maple Dr, Jacksonville, FL', source: 'Facebook', stage: 'appointment_set', priority: 'medium', appointment_date: new Date(now.getTime() + 259200000).toISOString(), created_at: new Date(now - 259200000).toISOString(), stage_changed_at: new Date(now - 172800000).toISOString() },
    { id: '5', contact_name: 'Robert Brown', phone: '(555) 567-8901', email: 'robert@email.com', address: '654 Cedar Ln, St. Petersburg, FL', source: 'Phone', stage: 'appointment_confirmed', priority: 'high', appointment_date: new Date(now.getTime() + 86400000).toISOString(), created_at: new Date(now - 345600000).toISOString(), stage_changed_at: new Date(now - 259200000).toISOString() },
    { id: '6', contact_name: 'Lisa Anderson', phone: '(555) 678-9012', email: 'lisa@email.com', address: '987 Birch Ct, Fort Lauderdale, FL', source: 'Website', stage: 'lead_issued', priority: 'medium', assigned_to_name: 'Sales Manager', created_at: new Date(now - 432000000).toISOString(), stage_changed_at: new Date(now - 345600000).toISOString() },
    { id: '7', contact_name: 'David Wilson', phone: '(555) 789-0123', email: 'david@email.com', address: '147 Walnut Way, Sarasota, FL', source: 'Referral', stage: 'appointment_cancelled', priority: 'low', created_at: new Date(now - 518400000).toISOString(), stage_changed_at: new Date(now - 432000000).toISOString() },
  ];
}
